import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';

const SESSION_TOKEN_KEY = 'pos_session_token';
const HEARTBEAT_INTERVAL = 5 * 60 * 1000; // 5 minutes

interface SessionState {
  isValid: boolean;
  isChecking: boolean;
  sessionToken: string | null;
  error: string | null;
}

export function useSessionManager() {
  const [sessionState, setSessionState] = useState<SessionState>({
    isValid: false,
    isChecking: true,
    sessionToken: null,
    error: null,
  });
  const heartbeatRef = useRef<NodeJS.Timeout | null>(null);

  // Create a new session after login
  const createSession = useCallback(async (deviceInfo?: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const { data, error } = await supabase.functions.invoke('manage-session', {
        body: {
          action: 'create',
          deviceInfo: deviceInfo || navigator.userAgent,
        },
      });

      if (error) {
        throw new Error(error.message);
      }

      if (data?.error) {
        return { 
          success: false, 
          error: data.message || data.error 
        };
      }

      if (data?.sessionToken) {
        localStorage.setItem(SESSION_TOKEN_KEY, data.sessionToken);
        setSessionState({
          isValid: true,
          isChecking: false,
          sessionToken: data.sessionToken,
          error: null,
        });
        startHeartbeat(data.sessionToken);
        return { success: true };
      }

      return { success: true }; // For standalone businesses without session tracking
    } catch (err: any) {
      console.error('Session creation error:', err);
      return { success: false, error: err.message };
    }
  }, []);

  // Validate existing session
  const validateSession = useCallback(async (): Promise<boolean> => {
    const sessionToken = localStorage.getItem(SESSION_TOKEN_KEY);
    if (!sessionToken) {
      setSessionState(prev => ({ ...prev, isValid: false, isChecking: false }));
      return false;
    }

    try {
      const { data, error } = await supabase.functions.invoke('manage-session', {
        body: {
          action: 'validate',
          sessionToken,
        },
      });

      if (error || !data?.valid) {
        localStorage.removeItem(SESSION_TOKEN_KEY);
        setSessionState({
          isValid: false,
          isChecking: false,
          sessionToken: null,
          error: 'Session expired or invalid',
        });
        return false;
      }

      setSessionState({
        isValid: true,
        isChecking: false,
        sessionToken,
        error: null,
      });
      startHeartbeat(sessionToken);
      return true;
    } catch (err) {
      console.error('Session validation error:', err);
      return false;
    }
  }, []);

  // Remove session on logout
  const removeSession = useCallback(async (): Promise<void> => {
    const sessionToken = localStorage.getItem(SESSION_TOKEN_KEY);
    if (sessionToken) {
      try {
        await supabase.functions.invoke('manage-session', {
          body: {
            action: 'remove',
            sessionToken,
          },
        });
      } catch (err) {
        console.error('Session removal error:', err);
      }
    }
    
    localStorage.removeItem(SESSION_TOKEN_KEY);
    stopHeartbeat();
    setSessionState({
      isValid: false,
      isChecking: false,
      sessionToken: null,
      error: null,
    });
  }, []);

  // Heartbeat to keep session alive
  const sendHeartbeat = useCallback(async (token: string) => {
    try {
      await supabase.functions.invoke('manage-session', {
        body: {
          action: 'heartbeat',
          sessionToken: token,
        },
      });
    } catch (err) {
      console.error('Heartbeat error:', err);
    }
  }, []);

  const startHeartbeat = useCallback((token: string) => {
    stopHeartbeat();
    heartbeatRef.current = setInterval(() => {
      sendHeartbeat(token);
    }, HEARTBEAT_INTERVAL);
  }, [sendHeartbeat]);

  const stopHeartbeat = useCallback(() => {
    if (heartbeatRef.current) {
      clearInterval(heartbeatRef.current);
      heartbeatRef.current = null;
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopHeartbeat();
    };
  }, [stopHeartbeat]);

  return {
    ...sessionState,
    createSession,
    validateSession,
    removeSession,
  };
}
